import { useState } from 'react'
import { Network, ShieldCheck, Server, Settings2 } from 'lucide-react'
import type { StructureData } from '../types.ts'
import { useAnonymize } from '@/hooks/useAnonymize.ts'
import { Card, Input, Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui'
import { AdExplorerTab } from './AdExplorerTab.tsx'
import { ServersTab } from './ServersTab.tsx'
import { UserGroupMatrix } from './UserGroupMatrix.tsx'

interface Props {
  data: StructureData
  update: (patch: Partial<StructureData>) => void
}

type SectionKey = 'grunddaten' | 'explorer' | 'admins' | 'servers'

const SECTIONS: Array<{
  key: SectionKey
  label: string
  hint: string
  icon: typeof Network
}> = [
  { key: 'grunddaten', label: 'Grunddaten',         hint: 'AD-Prefix, Domain, Beispiel-Gruppen-Schema',                          icon: Settings2 },
  { key: 'explorer',   label: 'AD-Explorer',        hint: 'OUs, Gruppen & User in einer Hierarchie — globale Items read-only',   icon: Network },
  { key: 'admins',     label: 'SysAdmin-Gruppen',   hint: 'SA-Gruppen-Membership — Verwaltung der IT-/ServiceDesk-Admins',       icon: ShieldCheck },
  { key: 'servers',    label: 'Server & Freigaben', hint: 'Fileserver + Shares (Laufwerksbuchstaben in Matrix)',                 icon: Server },
]

export function StandardsTab({ data, update }: Props) {
  const a = useAnonymize()
  const [activeSection, setActiveSection] = useState<SectionKey>('grunddaten')

  function setBase(field: 'domainPrefix' | 'domain', value: string) {
    update({ [field]: field === 'domainPrefix' ? value.toUpperCase() : value })
  }

  const active = SECTIONS.find((s) => s.key === activeSection)!

  return (
    <div className="space-y-3">
      <Card className="bg-brand-50 border-brand-100">
        <div className="px-4 py-2.5 text-caption text-brand-900">
          <span className="font-semibold">Standards-Tab</span> — fasst OUs, SysAdmin-Gruppen
          (überall gleich) und Server-Definitionen zusammen. Abteilungs-, Software-, Lizenz- und
          Funktions-Gruppen sowie Laufwerksbuchstaben werden in der <b>Zugriffsmatrix</b> gepflegt.
        </div>
      </Card>

      <Card>
        <Tabs value={activeSection} onValueChange={(v) => setActiveSection(v as SectionKey)}>
          <div className="px-2 bg-gray-50 border-b border-gray-200">
            <TabsList className="border-b-0">
              {SECTIONS.map(({ key, label, icon: Icon }) => (
                <TabsTrigger key={key} value={key}>
                  <Icon className="w-3.5 h-3.5" />
                  {label}
                </TabsTrigger>
              ))}
            </TabsList>
          </div>
          <div className="px-4 py-1.5 bg-gray-50 border-b border-gray-100 text-caption text-gray-400">
            {active.hint}
          </div>
          <div className="p-4">
            <TabsContent value="grunddaten" className="pt-0">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Input
                  label="AD-Prefix"
                  type="text"
                  value={a.token(data.domainPrefix ?? '')}
                  onChange={(e) => {
                    if (!a.enabled) setBase('domainPrefix', e.target.value)
                  }}
                  readOnly={a.enabled}
                  placeholder="z.B. KDK"
                  className="font-mono"
                  hint="Kundenkürzel — vor jeden AD-Gruppen- und OU-Namen."
                />
                <Input
                  label="AD-Domain"
                  type="text"
                  value={a.token(data.domain ?? '')}
                  onChange={(e) => {
                    if (!a.enabled) setBase('domain', e.target.value)
                  }}
                  readOnly={a.enabled}
                  placeholder="z.B. kunde.local"
                  className="font-mono"
                  hint="FQDN der Active-Directory-Domain."
                />
                <div className="flex flex-col gap-1">
                  <span className="text-small font-medium text-gray-700">Beispiel-Gruppe</span>
                  <div className="px-3 py-2 bg-white border border-gray-200 rounded-md text-body font-mono text-gray-500">
                    {a.token(data.domainPrefix || 'PREFIX')}_AB_Beispiel
                    {data.domain ? `@${a.token(data.domain)}` : ''}
                  </div>
                  <span className="text-caption text-gray-500">
                    Schema: <code>&lt;Prefix&gt;_&lt;Typ&gt;_&lt;Name&gt;@&lt;Domain&gt;</code>
                  </span>
                </div>
              </div>
            </TabsContent>
            <TabsContent value="explorer" className="pt-0">
              <AdExplorerTab data={data} update={update} embedded />
            </TabsContent>
            <TabsContent value="admins" className="pt-0">
              <UserGroupMatrix
                data={data}
                update={update}
                groupTypes={['SA']}
                activeTypes={['SA']}
                onActiveTypesChange={() => {
                  /* nur ein Typ — kein Filter */
                }}
              />
            </TabsContent>
            <TabsContent value="servers" className="pt-0">
              <ServersTab data={data} update={update} />
            </TabsContent>
          </div>
        </Tabs>
      </Card>
    </div>
  )
}
